"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { cn } from "@/lib/utils";

interface ScrollRevealTextProps {
  paragraphs: string[];
  className?: string;
}

export function ScrollRevealText({
  paragraphs,
  className,
}: ScrollRevealTextProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: textRef,
    offset: ["start end", "center center"],
  });

  // Calculate total characters across all paragraphs
  const totalChars = paragraphs.reduce((sum, p) => sum + p.length, 0);

  // Build character arrays with cumulative indices
  let charIndex = 0;
  const paragraphsWithChars = paragraphs.map((text) => {
    const chars = text.split("").map((char, i) => {
      const globalIndex = charIndex + i;
      return { char, globalIndex };
    });
    charIndex += text.length;
    return chars;
  });

  return (
    <div
      ref={textRef}
      className={cn(
        "font-arizona-sans text-[18px] leading-[1.5] text-neutral-950 tracking-[-0.36px]",
        className
      )}
    >
      {paragraphsWithChars.map((chars, pIndex) => (
        <p key={pIndex} className={pIndex > 0 ? "mt-6" : ""}>
          {chars.map(({ char, globalIndex }, cIndex) => {
            // Scale charProgress to 0-0.98 range to leave room for the +0.02 transition window
            // This ensures the last character can reach full opacity when scrollYProgress hits 1.0
            const charProgress = (globalIndex / totalChars) * 0.98;
            return (
              <ScrollRevealChar
                key={cIndex}
                char={char}
                charProgress={charProgress}
                scrollYProgress={scrollYProgress}
              />
            );
          })}
        </p>
      ))}
    </div>
  );
}

function ScrollRevealChar({
  char,
  charProgress,
  scrollYProgress,
}: {
  char: string;
  charProgress: number;
  scrollYProgress: ReturnType<typeof useScroll>["scrollYProgress"];
}) {
  const opacity = useTransform(
    scrollYProgress,
    [charProgress, charProgress + 0.02],
    [0.35, 1]
  );

  return <motion.span style={{ opacity }}>{char}</motion.span>;
}
